import React, { useState } from 'react';
import { CheckCircle2Icon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { DailyProgress } from '../components/queue/DailyProgress';
import { LeadWorkspace } from '../components/queue/LeadWorkspace';
import { ActivityTimeline } from '../components/queue/ActivityTimeline';
import { QueuePanel } from '../components/queue/QueuePanel';
import { EmailPanel } from '../components/queue/EmailPanel';
import { CallDialog } from '../components/queue/CallDialog';
import { useAppState } from '../contexts/AppState';
import { requiredCallsFor } from '../utils/format';
interface WorkQueueProps {
  layout?: 'split' | 'focused';
}
export function WorkQueue({
  layout = 'split'
}: WorkQueueProps) {
  const {
    queue,
    currentLead,
    publishedSources,
    publishedPriority,
    nextLead,
    logCall,
    logSms,
    logEmail,
    leadsWorkedToday,
    callsCompletedToday
  } = useAppState();
  const [emailOpen, setEmailOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const enabledPriority = publishedPriority.filter(r => r.enabled);
  const overdueCount = queue.reduce((sum, lead) => sum + lead.activities.filter(a => a.dueInMinutes !== undefined && a.dueInMinutes < 0).length, 0);
  const source = currentLead ? publishedSources.find(s => s.id === currentLead.sourceId) : undefined;
  const priorityRule = currentLead ? enabledPriority.find(r => r.id === currentLead.priorityRuleId) : undefined;
  const priorityIndex = currentLead ? enabledPriority.findIndex(r => r.id === currentLead.priorityRuleId) + 1 : 0;
  return <div className="space-y-5" data-id="element-465" data-mp="38sq2zsnc6lme">
      <div data-id="element-466" data-mp="3dr9r9zpek9hx">
        <h1 className="text-lg font-semibold tracking-tight text-ink-900" data-id="element-467" data-mp="3dqzy63929m80">Today&apos;s Work Queue</h1>
        <p className="mt-0.5 text-sm text-ink-500" data-id="element-468" data-mp="1eefexmekfvho">
          Leads are presented in the exact order set by management. Work the current lead, then
          advance.
        </p>
      </div>

      <DailyProgress remaining={queue.length} workedToday={leadsWorkedToday} callsCompleted={callsCompletedToday} overdueActivities={overdueCount} total={queue.length + leadsWorkedToday} data-id="element-469" data-mp="1bckld0yghwgg" />

      <div className={layout === 'focused' ? 'space-y-5' : 'grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]'} data-id="element-470" data-mp="1lbrz3p93rt43">
        <div className="space-y-5" data-id="element-471" data-mp="1uax2b8vm0aml">
          {currentLead ? <>
              <LeadWorkspace lead={currentLead} source={source} priorityIndex={priorityIndex} priorityLabel={priorityRule?.label ?? 'Cadence Lead'} priorityReason={priorityRule?.summary ?? ''} requiredCalls={requiredCallsFor(source, currentLead.ageMinutes)} ownerName="Sarah Johnson" onCall={() => setCallOpen(true)} onSms={() => logSms(currentLead.id)} onEmail={() => setEmailOpen(true)} onNext={nextLead} data-id="element-472" data-mp="1pclokn271fz3" />
              <ActivityTimeline activities={currentLead.activities} data-id="element-473" data-mp="2wng97crevk5u" />
            </> : <Card className="flex flex-col items-center justify-center px-6 py-20 text-center" data-id="element-474" data-mp="2io73j70u08g4">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700" data-id="element-475" data-mp="2nmru0eqa7drd">
                <CheckCircle2Icon className="h-6 w-6" aria-hidden="true" data-id="element-476" data-mp="28xvn4kzyo6sc" />
              </span>
              <h2 className="mt-4 text-lg font-semibold text-ink-900" data-id="element-477" data-mp="2dpdm53ifbxao">Queue complete</h2>
              <p className="mt-1 max-w-sm text-sm text-ink-500" data-id="element-478" data-mp="1zxpb73jwveva">
                Every lead has met today&apos;s required cadence. New leads will appear here
                automatically as they arrive.
              </p>
            </Card>}
        </div>

        <QueuePanel queue={queue} currentLeadId={currentLead?.id} sources={publishedSources} priority={publishedPriority} className={layout === 'focused' ? '' : 'xl:sticky xl:top-[88px] xl:max-h-[calc(100vh-120px)]'} data-id="element-479" data-mp="1q1clegrhll5t" />
      </div>

      {currentLead ? <>
          <EmailPanel open={emailOpen} lead={currentLead} onClose={() => setEmailOpen(false)} onSend={templateName => {
        logEmail(currentLead.id, templateName);
        setEmailOpen(false);
      }} data-id="element-480" data-mp="2oj72c5kp89kb" />
          <CallDialog open={callOpen} lead={currentLead} onClose={() => setCallOpen(false)} onLog={outcome => {
        logCall(currentLead.id, outcome);
        setCallOpen(false);
      }} data-id="element-481" data-mp="2eiwr2qi85c7s" />
        </> : null}
    </div>;
}